<script>
  import anime from 'animejs/lib/anime.es.js';
  import { CURRENCIES } from '@/store';

  import IconDollar from '@/assets/images/svg/icon-dollar.svg';
  import IconQcCoin from '@/assets/images/svg/icon-qc-coin.svg';
  import YellowButton from '@/components/base/YellowButton.vue';
  import breakpoints from '@/mixins/breakpoints';

  export default {
    name: 'BonusAndBalance',

    mixins: [breakpoints],

    components: {
      YellowButton,
      IconDollar,
      IconQcCoin,
    },

    props: {
      mini: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      isBalanceSelectOpen: false,
    }),
    computed: {
      currencies() {
        return [
          {
            type: CURRENCIES.MMC,
            balance: this.balance.mmc,
            color: 'white',
            icon: IconDollar,
          },
          {
            type: CURRENCIES.MQC,
            balance: this.balance.mqc,
            color: 'gold',
            icon: IconQcCoin,
          },
        ];
      },

      balance() {
        return this.$store.getters.getFormattedBalance;
      },

      playerMode() {
        return this.$store.state.playermode;
      },

      selectedCurrency() {
        return {
          icon: this.playerMode === CURRENCIES.MMC ? 'IconDollar' : 'IconQcCoin',
          balance: this.balance[this.playerMode.toLowerCase()],
          color: this.playerMode === CURRENCIES.MMC ? 'white' : 'gold',
        };
      },

      isUserSuspended() {
        return this.$store.getters.isUserSuspended;
      },
    },

    watch: {
      mini(newVal) {
        if (!newVal) {
          this.animateItems();
        }
      },
    },

    methods: {
      animateItems() {
        anime({
          targets: this.$refs.bonusAndBalanceContainer.children,
          translateX: [-50, 0],
          opacity: [0, 1],
          delay: anime.stagger(20),
          duration: 200,
          easing: 'easeOutQuad',
        });
      },

      openVault(tab) {
        this.$store.dispatch('setWalletDialog', {
          tab,
          value: true,
        });
      },

      setSelected(currencyType) {
        this.$store.commit('setPlayerMode', currencyType);
      },
      closeBalanceSelect() {
        this.isBalanceSelectOpen = false;
      },
    },
  };
</script>

<template>
  <div ref="bonusAndBalanceContainer" :class="{ mini }" class="container bonus-and-balance-container">
    <div v-if="!mini" class="balance-wrapper">
      <v-menu v-model="isBalanceSelectOpen" content-class="top-nav-dropdown top-nav-dropdown--sidebar" offset-y>
        <template v-slot:activator="{ on }">
          <div class="decoration" />
          <v-btn class="selected-currency" height="48" v-on="on">
            <component :is="selectedCurrency.icon" class="currency-icon" />
            <span :class="`${selectedCurrency.color}--text`" class="currency-amount">
              {{ selectedCurrency.balance }}
            </span>
            <v-icon :size="isMobileOrTablet ? 'small' : 'default'">mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item v-for="currency in currencies" :key="currency.type" @click="setSelected(currency.type)">
            <div class="user-balance">
              <component :is="currency.icon" class="currency-icon" />
              <span :class="`${currency.color}--text`">
                {{ currency.balance }}
              </span>
            </div>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
    <v-btn v-if="!isUserSuspended" :class="{ mini }" class="buy-coins-btn" @click="openVault('purchase')">
      <span :class="{ 'ml-2': !mini, mini }">Deposit</span>
    </v-btn>
    <v-btn v-if="!isUserSuspended" :class="{ mini }" class="redeem-btn" @click="openVault('withdrawal')">
      Withdraw
    </v-btn>
  </div>
</template>

<style lang="scss" scoped>
  .container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0;
    margin-bottom: 24px;

    &.mini {
      padding-right: 4px;
    }
  }

  .button-base {
    width: 100%;
    height: 48px !important;
    border-radius: 6px;
    color: white;
    font-weight: 700;
    font-size: 16px;
    text-transform: none;
    transition: unset !important;

    &.mini {
      height: 40px !important;
      min-width: 60px !important;
      font-size: 12px;
    }
  }

  .buy-coins-btn {
    @extend .button-base;
    background: var(--v-gold-base) !important;
    color: #140324;

    &:hover {
      filter: brightness(108%);
    }
  }

  .redeem-btn.v-btn {
    @extend .button-base;
    border: 1px solid rgba(255, 255, 255, 0.4);
    background-color: rgba(255, 255, 255, 0.07);
  }

  .buy-coins-btn,
  .redeem-btn {
    &.v-btn.v-btn--disabled {
      filter: grayscale(1);
      pointer-events: none;
      border: 0;
      background-color: #58515e !important;
    }
  }

  .balance-wrapper {
    display: flex;
    height: 48px;
    align-items: center;
    gap: 10px;
    align-self: stretch;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    background: rgba(0, 0, 0, 0.3);
    overflow: hidden;

    .decoration {
      width: 4px;
      align-self: stretch;
      background: #f8bf1e;
    }

    svg {
      width: 30px;
      height: 30px;
    }
  }

  .balance-value {
    color: #f8bf1e;
    text-align: center;
    font-size: 18px;
    font-weight: 500;
    line-height: 100%;
  }

  .selected-currency.v-btn {
    display: flex;
    background: transparent;
    padding: 0;
    width: calc(100% - 24px);

    .currency-amount {
      text-align: center;
      font-family: 'Barlow', sans-serif;
      font-size: 18px;
      font-weight: 500;
      line-height: 100%;
    }

    svg {
      width: 30px;
      flex-grow: 0;
    }

    .v-icon {
      color: #fff;
      opacity: 0.4;
      margin-left: auto;
    }

    ::v-deep .v-btn__content {
      gap: 10px;
    }

    ::v-deep .v-ripple__container {
      display: none;
    }
  }
</style>

<style lang="scss">
  .top-nav-dropdown--sidebar .v-list {
    min-width: 333px;
  }
  @media (max-width: 600px) {
    .top-nav-dropdown--sidebar .v-list {
      min-width: calc(77vw - 34px);
    }
  }
</style>
